import { fromJS, List, Map } from 'immutable';
import { LiveMatch } from '../../../dto';
import { Delta, PushEvent } from '../../../dto/PushEnvelope';
import {
    createOrderedIndex,
    indexById,
    indexGroupedAndOrdered,
    insertToOrderedIndex,
    mergeOrderedIndex,
    OrderedIndex,
    removeFromOrderedIndex,
    updateIndexedById
} from '../../indexingUtils';

export function onMatchesSnapshot(matches: LiveMatch[]) {
    return (db: Map<string, any>) =>
        db
            .setIn(['matches'], indexById(matches))
            .setIn(['matchIdsByTournament'], indexMatchesByTournament(matches))
            .setIn(['matchIdsByStartTime'], createOrderedIndex(matches, 'startTime'));
}

export function onMatchesSnapshotMerge(matches: LiveMatch[]) {
    return (db: Map<string, any>) =>
        db
            .mergeIn(['matches'], indexById(matches))
            .updateIn(['matchIdsByTournament'], Map(), (matchIdsByTournament: Map<string, List<OrderedIndex>>) =>
                matchIdsByTournament.mergeWith(mergeOrderedIndex, indexMatchesByTournament(matches))
            )
            .updateIn(['matchIdsByStartTime'], List(), matchIdsByStartTime =>
                mergeOrderedIndex(matchIdsByStartTime, createOrderedIndex(matches, 'startTime'))
            );
}

export function onMatchPush(event: PushEvent, data: LiveMatch | Delta | { id: string }) {
    switch (event) {
        case PushEvent.CREATED:
            return onCreated(data as LiveMatch);
        case PushEvent.UPDATED:
            return updateIndexedById(data as Delta, 'matches');
        case PushEvent.DELETED:
            return onDeleted(data.id);
        default:
            console.error(`Invalid PushEvent ${event}`);
            return turboLiveDb => turboLiveDb;
    }
}

function onCreated(match: LiveMatch) {
    return turboLiveDb => {
        const { id, tournamentId, startTime } = match;

        return turboLiveDb
            .update('matches', Map(), matches => matches.set(id, fromJS(match)))
            .update('matchIdsByStartTime', List(), insertToOrderedIndex({ id, order: startTime }))
            .update('matchIdsByTournament', Map(), matchIdsByTournament =>
                matchIdsByTournament.update(tournamentId, List(), insertToOrderedIndex({ id, order: startTime }))
            );
    };
}

function onDeleted(matchId: string) {
    return turboLiveDb => {
        const match = turboLiveDb.get('matches').get(matchId);
        if (!match) {
            console.warn(`Match ${matchId} not found, cannot handle DELETE push event.`, { turboLiveDb });
            return turboLiveDb;
        }
        const tournamentId = match.get('tournamentId');

        return turboLiveDb
            .update('matches', Map(), matches => matches.delete(matchId))
            .update('matchIdsByStartTime', List(), removeFromOrderedIndex(matchId))
            .update('matchIdsByTournament', Map(), matchIdsByTournament =>
                matchIdsByTournament.update(tournamentId, List(), removeFromOrderedIndex(matchId))
            );
    };
}

export function indexMatchesByTournament(matches: LiveMatch[]) {
    return indexGroupedAndOrdered(matches, 'tournamentId', 'startTime');
}

export const clearMbm = db =>
    db
        .removeIn(['turboLive', 'detail', 'mbm'])
        .removeIn(['turboLive', 'detail', 'mbmIdsByOrder'])
        .removeIn(['turboLive', 'detail', 'mbmLoaded']);
